import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getProvider } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { mapApiProviderToUi } from '../../utils';

/**
 * Provider Detail data (CT-4186): fetches the provider by slug and maps it to the
 * view model all tabs read via `useOutletContext()`. Race/unmount-safe.
 */
export function useProvider(slug) {
  const { t } = useTranslation('admin-studio');
  const [provider, setProvider] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getProvider(slug);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      const body = validateApiResponse(res, t('common.requestFailed')).body;
      const mapped = mapApiProviderToUi(body);
      if (!mapped) {
        setError(t('common.requestFailed'));
        setProvider(null);
        return;
      }
      setProvider(mapped);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
      setProvider(null);
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => ({ provider, isLoading, error, refetch: load }), [provider, isLoading, error, load]);
}
